/** Session-expiry policy: when to renew silently, when to warn, and how to render
 *  the countdown.
 *
 *  Everything here is a pure function of the current time. The composable that
 *  drives it owns the timers and the DOM listeners, which keeps the decisions
 *  themselves testable without a browser.
 */

/** Must agree with `refreshTokenValidity` on the user pool client
 *  (infra/lib/church-admin-stack.ts). Once this much has elapsed since sign-in no
 *  refresh can succeed, so the session ends for real and the warning must stop
 *  offering a button that cannot work. */
export const REFRESH_WINDOW_MS = 8 * 60 * 60 * 1000;

/** How long before token expiry the countdown begins. */
export const WARN_BEFORE_MS = 5 * 60 * 1000;

/** Someone who has touched the page within this window counts as active, and their
 *  session is renewed silently rather than by interrupting them. A warning nobody
 *  needs is a warning people learn to dismiss unread. */
export const IDLE_AFTER_MS = 2 * 60 * 1000;

export type SessionPhase = 'active' | 'renew' | 'warn' | 'expired';

export interface SessionInput {
  now: number;
  /** Epoch milliseconds at which the ID token expires; null when signed out. */
  expiresAt: number | null;
  /** Epoch milliseconds of the original sign-in; null for sessions that pre-date
   *  this feature and so have no recorded start. */
  sessionStartedAt: number | null;
  lastActivityAt: number;
}

export interface SessionState {
  phase: SessionPhase;
  msRemaining: number;
  /** Whether a refresh could still succeed. Drives whether the warning offers
   *  "Stay signed in" or simply says the session has ended. */
  canExtend: boolean;
}

export function canRefresh(now: number, sessionStartedAt: number | null): boolean {
  // Assumption: a session with no recorded start pre-dates this feature, so assume a
  // refresh is worth attempting and let a real failure settle the question. The
  // alternative — refusing to try — would sign people out who could have continued.
  if (sessionStartedAt === null) return true;
  return now < sessionStartedAt + REFRESH_WINDOW_MS;
}

export function resolveSessionState(input: SessionInput): SessionState {
  const { now, expiresAt, sessionStartedAt, lastActivityAt } = input;

  // No decoded expiry means there is no session to police; the router guard owns
  // that case.
  if (expiresAt === null) {
    return { phase: 'active', msRemaining: 0, canExtend: false };
  }

  const msRemaining = Math.max(0, expiresAt - now);
  const canExtend = canRefresh(now, sessionStartedAt);

  if (msRemaining === 0) return { phase: 'expired', msRemaining, canExtend };
  if (msRemaining > WARN_BEFORE_MS) return { phase: 'active', msRemaining, canExtend };

  const isActive = now - lastActivityAt < IDLE_AFTER_MS;
  if (isActive && canExtend) return { phase: 'renew', msRemaining, canExtend };

  return { phase: 'warn', msRemaining, canExtend };
}

/** Formats the remaining time as MM:SS. The warning window is minutes, not hours,
 *  so HH: would be three characters of nothing. Rounds up so the countdown shows
 *  the full "05:00" when it opens and never reads 00:00 while time is left. */
export function formatCountdown(msRemaining: number): string {
  const totalSeconds = Math.max(0, Math.ceil(msRemaining / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}
